/* eslint-disable consistent-return */
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import { selectAuth } from '@brands/store/selectors/auth';
import { stateOptions } from '@brands/Utils/selectOptions';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { AiOutlineClose } from 'react-icons/ai';
import { CgProfile } from 'react-icons/cg';
import { MdKeyboardArrowLeft } from 'react-icons/md';
import Modal from 'react-modal';

import { useWindowSize } from '../../../hooks';
import { useAppSelector } from '../../../hooks/useReduxHook';
import { getOneCase } from '../../../services/cases/getCaseById';
import { ICase } from '../../../services/cases/types/ICase';
import { getUserAvatar } from '../../../services/identity/getUserAvatar';
import { Language, License, UserProfile, UserRoleName } from '../../../services/identity/types/UserProfile';
import { customStylesDashboard } from '../../../Utils/customStyles';
import { displayErrorDetails } from '../../../Utils/displayError';
import { ProviderFilter } from '../../ConsultationPage/assets/types';
import CareAdvisorView from './components/CareAdvisorView';
import styles from './styles.module.scss';

interface ProfileCardModalProps {
  isOpen: boolean;
  toggleModal: (bool: boolean) => void;
  providerIdentity: UserProfile | null;
  caseId?: number;
  patientCurrentStateValue?: string | undefined;
  filters?: ProviderFilter | undefined;
  thisCase?: ICase;
  setCase?: React.Dispatch<React.SetStateAction<ICase>>;
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  openModal?: (arg0: boolean) => void;
  isAssignProvider?: boolean;
}

const desktopModalStyles = {
  ...customStylesDashboard,
  overlay: {
    ...customStylesDashboard.overlay,
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
  },
  content: {
    ...customStylesDashboard.content,
    borderRadius: '4px',
    boxShadow: '0 8px 10px rgba(0,0,0,0.20), 0 16px 24px rgba(0,0,0,0.14), 0 6px 30px rgba(0,0,0,0.12)',
    padding: '0',
    border: 'none',
    background: '#ffffff',
  },
};

const mobileCustomStylesDashboard = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 9999,
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    width: '95vw',
    height: '78vh',
    maxHeight: '90vh',
    overflowY: 'auto' as const,
    zIndex: 10000,
    borderRadius: '4px',
    boxShadow: '0 8px 10px rgba(0,0,0,0.20), 0 16px 24px rgba(0,0,0,0.14), 0 6px 30px rgba(0,0,0,0.12)',
    padding: '0',
  },
};

const EMPTY_VALUE = '-';

const titleize = (value: string): string =>
  value
    .split(' ')
    .map((word) => (word ? `${word.charAt(0).toUpperCase()}${word.slice(1)}` : word))
    .join(' ');

const formatList = (values?: string[], transform: (value: string) => string = (value) => value): string => {
  const formattedValues = values?.map((value) => transform(value.trim())).filter(Boolean) || [];
  return formattedValues.length > 0 ? formattedValues.join(', ') : EMPTY_VALUE;
};

const formatLanguages = (languages?: (Language | string)[]): string => {
  const formattedLanguages =
    languages
      ?.map((language) => (typeof language === 'string' ? language : language.name))
      .map((language) => language?.trim())
      .filter(Boolean) || [];
  return formattedLanguages.length > 0 ? formattedLanguages.join(', ') : EMPTY_VALUE;
};

const ProviderProfileCardModal: FC<ProfileCardModalProps> = ({
  isOpen,
  toggleModal,
  providerIdentity,
  caseId = 0,
  patientCurrentStateValue,
  filters,
  thisCase,
  setCase,
  setLoading,
  openModal,
  isAssignProvider = false,
}) => {
  const isMobile = useWindowSize().width < 768;
  const { userInfo } = useAppSelector(selectAuth);
  const isCA = userInfo.role.name === UserRoleName.CareAdvisor;
  const [providerIdentityFromCase, setProviderIdentity] = useState<UserProfile | null>(null);
  const [patientCurrentState, setPatientCurrentState] = useState(patientCurrentStateValue || '');
  const [relatedCase, setRelatedCase] = useState<ICase>();
  const [avatarIcon, setAvatarIcon] = useState(false);
  const [loadingState, setLoadingState] = useState(false);
  const providerProfile = useMemo(
    () => (providerIdentity?.basic_info !== undefined ? providerIdentity : providerIdentityFromCase),
    [providerIdentity, providerIdentityFromCase],
  );

  const initIdentity = async (): Promise<void> => {
    try {
      setLoadingState(true);
      if (caseId && (providerIdentity?.basic_info === undefined || patientCurrentStateValue === undefined || isCA)) {
        const thisCurrentCase = await getOneCase(caseId);
        if (thisCurrentCase) {
          setRelatedCase(thisCurrentCase);
          setProviderIdentity(thisCurrentCase.provider);
          setPatientCurrentState(thisCurrentCase.patient_current_state || '');
        }
      } else if (patientCurrentStateValue) {
        setPatientCurrentState(patientCurrentStateValue);
      }
    } catch (error: unknown) {
      displayErrorDetails(error);
    } finally {
      setLoadingState(false);
    }
  };

  const licenseNumberDisplay = useMemo(() => {
    const isPatient = userInfo.role.name === UserRoleName.Patient;
    const licenses = providerProfile?.provider_profile?.licenses?.filter((license) => license.number);

    if (!licenses || licenses.length === 0) return EMPTY_VALUE;

    if (isPatient) {
      return licenses.find((license: License) => license.state === patientCurrentState)?.number || EMPTY_VALUE;
    }
    return licenses.map((license: License) => license.number).join(', ') || EMPTY_VALUE;
  }, [patientCurrentState, providerProfile, userInfo.role.name]);

  const getLicenseStates = (provider: UserProfile | null): string => {
    const nonExpiredLicenses = provider?.provider_profile?.licenses?.filter(
      (license) => new Date(license.expiry_date) > new Date(),
    );
    const licenseStates =
      nonExpiredLicenses
        ?.map(
          (license) =>
            stateOptions.find((option) => option.value === license.state)?.label.split(' - ')[1] || license.state,
        )
        .filter(Boolean) || [];
    return licenseStates.length > 0 ? licenseStates.join(', ') : EMPTY_VALUE;
  };

  const licenseStatesDisplay = getLicenseStates(providerProfile);
  const providerCredentials = providerProfile?.provider_profile?.credentials || providerProfile?.credentials || '';
  const providerNameDisplay = `${providerProfile?.full_name || EMPTY_VALUE}${
    providerCredentials ? `, ${providerCredentials}` : ''
  }${providerProfile?.status === 'Deactivated' ? ' (Deactivated)' : ''}`;
  const biographyDisplay = providerProfile?.provider_profile?.biography?.trim() || EMPTY_VALUE;
  const educationDisplay = formatList(providerProfile?.provider_profile?.education);
  const specialtyDisplay = formatList(providerProfile?.provider_profile?.specialty, titleize);
  const languagesDisplay = formatLanguages(providerProfile?.basic_info?.languages);

  useEffect(() => {
    initIdentity();
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
    } else if (document.body.classList.contains('modal-open') && !isOpen) {
      document.body.classList.remove('modal-open');
    }
  }, [isOpen]);
  const closeModal = (): void => {
    toggleModal(false);
  };

  const handleAvatarError = (): void => {
    setAvatarIcon(true);
  };

  const renderProfileContent = (showCareAdvisorActions: boolean): JSX.Element => (
    <div className={styles.contentArea}>
      <div className={styles.profileContainer}>
        <div className={styles.imageContainer}>
          {!avatarIcon ? (
            <img
              alt="Avatar"
              onError={handleAvatarError}
              className={`fs-exclude ${styles.providerImage}`}
              src={getUserAvatar(providerProfile?.id || providerIdentity?.id || 0)}
            />
          ) : (
            <CgProfile className="fs-exclude" />
          )}
        </div>
        <div className={styles.textContainer}>
          <div className={`fs-exclude ${styles.title}`}>{providerNameDisplay}</div>
        </div>
        <div className={styles.providerDetails}>
          <div className={`fs-unmask ${styles.title}`}>License number</div>
          <div className={`fs-exclude ${styles.value}`}>{licenseNumberDisplay}</div>
        </div>
        <div className={styles.providerDetails}>
          <div className={`fs-unmask ${styles.title}`}>License States</div>
          <div className={`fs-exclude ${styles.value}`}>{licenseStatesDisplay}</div>
        </div>
        <div className={styles.providerDetails}>
          <div className={`fs-unmask ${styles.title}`}>Provider NPI #</div>
          <div className={`fs-exclude ${styles.value}`}>
            {providerIdentity?.provider_profile?.national_provider_id ||
              providerIdentityFromCase?.provider_profile?.national_provider_id ||
              '-'}
          </div>
        </div>
      </div>
      <hr className={styles.divider} />
      <div className={styles.profileCardBody}>
        <div className={styles.bio}>
          <div className={`fs-unmask ${styles.title}`}>About me</div>
          <div className={`fs-exclude ${styles.content}`}>{biographyDisplay}</div>
        </div>
        <div className={styles.education}>
          <div className={`fs-unmask ${styles.title}`}>Education</div>
          <div className={`fs-exclude ${styles.content}`}>{educationDisplay}</div>
        </div>
        <div className={styles.primary}>
          <div className={`fs-unmask ${styles.title}`}>Primary Specialties</div>
          <div className={`fs-exclude ${styles.content}`}>{specialtyDisplay}</div>
        </div>
        <div className={styles.languages}>
          <div className={`fs-unmask ${styles.title}`}>Languages</div>
          <div className={`fs-exclude ${styles.content}`}>{languagesDisplay}</div>
        </div>
      </div>
      {showCareAdvisorActions && isCA && relatedCase && isAssignProvider && (
        <CareAdvisorView
          relatedCase={relatedCase}
          filters={filters}
          thisCase={thisCase}
          setCase={setCase}
          setLoading={setLoading}
          providerId={providerProfile?.id}
          openModal={openModal}
          isAssignProvider={isAssignProvider}
        />
      )}
    </div>
  );

  if (isMobile) {
    return (
      <Modal
        isOpen={isOpen}
        onRequestClose={closeModal}
        style={mobileCustomStylesDashboard}
        contentLabel="Modal"
        ariaHideApp={false}
      >
        <div className={styles.profileDashboardContainer}>
          <div className={styles.profileCardProvider}>
            <button type="button" className={styles.backButton} onClick={closeModal} aria-label="Back">
              <MdKeyboardArrowLeft />
              Back
            </button>
            {loadingState ? <Loading /> : renderProfileContent(true)}
          </div>
        </div>
      </Modal>
    );
  }
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      style={desktopModalStyles}
      contentLabel="Modal"
      ariaHideApp={false}
    >
      <div className={styles.profileCardProvider}>
        <div className={styles.titleBar}>
          <div className={styles.titleBarLeft}>
            <span className={styles.purpleChip}>Provider</span>
          </div>
          <button
            type="button"
            className={`fs-unmask ${styles.modalClose}`}
            data-micromodal-close=""
            onClick={closeModal}
            aria-label="Close modal"
          >
            <AiOutlineClose className={styles.icon} />
          </button>
        </div>
        <button type="button" className={styles.backButton} onClick={closeModal} aria-label="Back">
          <MdKeyboardArrowLeft />
          Back
        </button>
        {loadingState ? <Loading /> : renderProfileContent(false)}
      </div>
    </Modal>
  );
};

Modal.setAppElement('#root');

export default ProviderProfileCardModal;
